import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api, cn } from "../lib/api";
import { Card, Skeleton } from "./ui";
import { withUnit } from "./ClubLeaderboards";

const SHOWN = [
  { key: "mvp", label: "MVP", emoji: "🏆" },
  { key: "batting", label: "Top batter", emoji: "🏏" },
  { key: "bowling", label: "Top bowler", emoji: "🎯" },
  { key: "fielding", label: "Top fielder", emoji: "🧤" },
];

/**
 * Home screen snapshot of the club leaderboards: this month's leaders (or all time when
 * nothing's been played this month yet) and where the player ranks.
 */
export default function ClubLeadersCard() {
  const nav = useNavigate();
  const [data, setData] = useState(undefined); // undefined = loading, null = nothing to show

  useEffect(() => {
    let alive = true;
    const hasLeaders = (d) => d.boards.some((b) => b.rows.length > 0);
    (async () => {
      try {
        const { data: month } = await api.get("/stats/club", { params: { period: "month" } });
        if (hasLeaders(month)) return alive && setData(month);
        const { data: all } = await api.get("/stats/club", { params: { period: "all" } });
        alive && setData(hasLeaders(all) ? all : null);
      } catch {
        alive && setData(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (data === undefined) {
    return <div className="px-5 mt-6"><Skeleton className="h-48" /></div>;
  }
  if (data === null) return null; // no scored matches yet — nothing meaningful to show

  const boards = Object.fromEntries(data.boards.map((b) => [b.key, b]));
  const mvp = boards.mvp?.rows || [];
  const myRank = mvp.findIndex((r) => r.member_id === data.me);

  return (
    <div className="px-5 mt-6" data-testid="club-leaders">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
          Club leaders • {data.period === "month" ? "this month" : "all time"}
        </div>
        <button onClick={() => nav("/stats?tab=club")} className="text-xs font-semibold text-brand flex items-center" data-testid="club-leaders-all">
          All stats <ChevronRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {SHOWN.map(({ key, label, emoji }) => {
          const b = boards[key];
          const top = b?.rows[0];
          return (
            <Card
              key={key}
              className={cn("p-4 min-w-0 cursor-pointer btn-press", top?.member_id === data.me && "ring-2 ring-brand/30")}
              onClick={() => nav("/stats?tab=club")}
            >
              <div className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold flex items-center gap-1">
                <span aria-hidden>{emoji}</span> {label}
              </div>
              {top ? (
                <>
                  <div className="font-bold truncate mt-1">{top.name}{top.member_id === data.me && <span className="text-[10px] text-brand ml-1">YOU</span>}</div>
                  <div className="text-sm text-ink-muted tabular-nums">
                    <span className="font-extrabold text-ink">{top.value}</span> {withUnit(top.value, b.unit).split(" ").slice(1).join(" ")}
                  </div>
                </>
              ) : (
                <div className="text-sm text-ink-muted mt-1">—</div>
              )}
            </Card>
          );
        })}
      </div>

      {data.me && (
        <div className="mt-3 rounded-2xl bg-brand-50 text-brand-700 text-sm px-4 py-3 flex items-center justify-between gap-2">
          <span>
            {myRank >= 0
              ? <>You're <b>#{myRank + 1}</b> in the MVP table with <b>{mvp[myRank].value} pts</b></>
              : mvp.length >= 10 ? "Keep going — the MVP table shows the top 10" : "Play a match to get on the leaderboard"}
          </span>
          {myRank === 0 && <span aria-hidden>🥇</span>}
        </div>
      )}
    </div>
  );
}
